// Manual GPS clock-in for field workers — tap "on site" to stamp time +
// location (no QR poster or geofence needed). Stamps flow into the site
// sign-in register and prefill the worker's timesheet start/end times.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { MapPin, LogOut, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
  );
});

const fmtHM = (iso) => new Date(iso).toLocaleTimeString("en-NZ", {
  timeZone: "Pacific/Auckland", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
});

export function ClockInCard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const load = () => api.get("/clock/status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const clockIn = async () => {
    setBusy(true);
    try {
      const pos = await getPosition();
      const { data } = await api.post("/clock/in", pos || {});
      if (!pos) toast.message("Clocked in without GPS — location permission was off");
      else toast.success(`Clocked in at ${fmtHM(data.signed_in_at)}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clock-in failed");
    } finally { setBusy(false); }
  };

  const clockOut = async () => {
    setBusy(true);
    try {
      const pos = await getPosition();
      const { data } = await api.post("/clock/out", pos || {});
      const params = new URLSearchParams({ new: "1", clock: "1", date: data.date, start_time: fmtHM(data.signed_in_at), end_time: fmtHM(data.signed_out_at) });
      if (data.job_id) params.set("job_id", data.job_id);
      const day = iso => new Date(iso).toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland" });
      if (day(data.signed_in_at) !== day(data.signed_out_at)) params.set("overnight", "1");
      toast.success("Clocked out — review your timesheet and break before submitting");
      navigate(`/app/timesheets?${params}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clock-out failed");
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const open = status.open;

  if (open) {
    const mins = Math.max(0, Math.round((Date.now() - new Date(open.signed_in_at).getTime()) / 60000));
    const elapsed = `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
    return (
      <div className="mb-4 rounded-2xl bg-white dark:bg-[#101620] text-zinc-900 dark:text-white border border-zinc-200 dark:border-white/10 border-l-4 border-l-emerald-500" data-testid="clock-card-on-site">
        <div className="flex items-center justify-between px-4 py-3.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> On site
            </div>
            <div className="font-display font-extrabold uppercase text-lg leading-tight mt-0.5" data-testid="clock-elapsed">
              Since {fmtHM(open.signed_in_at)} · {elapsed}
            </div>
            {open.job_title && <div className="text-[11px] text-zinc-500 dark:text-zinc-400 font-mono truncate">{open.job_title}</div>}
          </div>
          <button
            onClick={clockOut}
            disabled={busy}
            className="shrink-0 bg-white text-black px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 active:scale-95 transition-transform"
            data-testid="clock-out-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> {busy ? "…" : "Clock out"}
          </button>
        </div>
      </div>
    );
  }

  if (status.departure) {
    return (
      <div className="mb-4 rounded-2xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] px-4 py-3 flex items-center justify-between gap-3" data-testid="clock-card-done">
        <div className="flex items-center gap-2 text-xs font-mono">
          <Clock className="w-4 h-4 text-[#FF5F15]" />
          <span className="text-zinc-500 uppercase tracking-wider">Day stamped</span>
          <b className="text-zinc-900 dark:text-white">{status.arrival}–{status.departure}</b>
        </div>
        <Link to="/app/timesheets?new=1" className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] flex items-center gap-1" data-testid="clock-log-hours-link">
          Log hours <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  return (
    <button
      onClick={clockIn}
      disabled={busy}
      className="mb-4 w-full rounded-[20px] bg-white dark:bg-[#101620] text-zinc-900 dark:text-white border border-zinc-200 dark:border-[#1F2937] flex items-center justify-between px-5 py-5 active:scale-[0.98] transition-transform text-left shadow-lg shadow-black/5"
      data-testid="clock-in-btn"
    >
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF5F15]">Arrived at work?</div>
        <div className="font-display font-extrabold uppercase text-3xl leading-none mt-2">{busy ? "Stamping…" : "Clock in"}</div>
        <div className="text-[12px] text-zinc-500 dark:text-[#94A3B8] mt-2">Let us know you&rsquo;re on site</div>
      </div>
      <span className="w-16 h-16 rounded-full border-2 border-[#FF5F15] bg-[#FF5F15]/10 flex items-center justify-center shrink-0 shadow-[0_0_28px_-4px_rgba(255,95,21,0.55)]">
        <MapPin className="w-6 h-6 text-[#FF5F15]" />
      </span>
    </button>
  );
}
